import { useNavigate } from 'react-router-dom';
import { Suspense, lazy, useState, useEffect, useRef, useCallback } from 'react';
import AddressAutocomplete from '../../components/common/AddressAutocomplete';
import { deriveAddressFormValues, buildAddressPayload } from '../../utils/profileFormValues';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import api from '../../services/api';
import styles from './ProfilePage.module.css';
import { formatDate, formatPrice } from '../../utils/formatters';

/**
 * Card 52887: with the payment switch ON this page stops collecting the card number, expiry
 * and CVC and hands the whole job to the provider's own hosted Element, so none of the three
 * ever reaches an Oval origin. With the switch OFF — today's posture, and the default for any
 * deployment that has not opted in — every field and every request below is unchanged.
 *
 * 🔑 THE PROVIDER COMPONENT IS LAZY, AND THAT IS A REQUIREMENT, NOT A SIZE OPTIMISATION.
 * A static import puts the provider SDK in the main chunk of EVERY build, including a
 * switched-off one, which would make an OFF client fetch a provider host on load — precisely
 * what the OFF posture is asserted not to do. Lazy keeps it in a chunk fetched only after the
 * config has been read as enabled.
 */
const StripePaymentElement = lazy(() => import('../../components/forms/StripePaymentElement'));

const ProfilePage = () => {
  const { user, updateUser, isDemo, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [membershipData, setMembershipData] = useState(null);
  const [loadingMembership, setLoadingMembership] = useState(false);
  const [requestingUpgrade, setRequestingUpgrade] = useState(false);
  const upgradeInFlight = useRef(false);

  const [profileData, setProfileData] = useState({
    firstName: user?.profile?.firstName || '',
    lastName: user?.profile?.lastName || '',
    email: user?.email || '',
    dateOfBirth: user?.profile?.dateOfBirth?.split('T')[0] || '',
    phone: user?.profile?.phone || '',
    gender: user?.profile?.gender || '',
    weight: user?.profile?.weight || '',
    height: user?.profile?.height || '',
    ...deriveAddressFormValues(user)
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // ID Verification state
  const [idVerification, setIdVerification] = useState(null);
  const [loadingId, setLoadingId] = useState(false);
  // Card 53577: a FAILED fetch is not "no ID on file". Without this the catch below left
  // `idVerification` at its initial null and the panel stated, as fact, that this member has
  // no ID — to a member whose ID we simply failed to read.
  const [idLoadError, setIdLoadError] = useState(false);
  const [uploadingId, setUploadingId] = useState(false);
  const frontInputRef = useRef(null);
  const backInputRef = useRef(null);
  const [dragOverFront, setDragOverFront] = useState(false);
  const [dragOverBack, setDragOverBack] = useState(false);
  const [frontPreview, setFrontPreview] = useState(null);
  const [backPreview, setBackPreview] = useState(null);
  const [pendingFront, setPendingFront] = useState(null);
  const [pendingBack, setPendingBack] = useState(null);
  const [savingId, setSavingId] = useState(false);

  // Payment state
  const [paymentMethods, setPaymentMethods] = useState({ cards: [], ach: null });
  const [loadingPayments, setLoadingPayments] = useState(false);
  // Card 53577: a FAILED fetch is not an empty wallet. Left unset it renders "No payment
  // methods saved yet." beside an Add control, inviting a member to re-enter a card they
  // already have — a duplicate payment profile on the money path.
  const [paymentsLoadError, setPaymentsLoadError] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentType, setPaymentType] = useState('card');
  const [savingPayment, setSavingPayment] = useState(false);
  const [cardData, setCardData] = useState({ number: '', expiration: '', cvv: '', nameOnCard: '' });
  const [sameAsShippingNew, setSameAsShippingNew] = useState(true);
  const [newBillingAddress, setNewBillingAddress] = useState({ street: '', city: '', state: '', zip: '' });
  const [achData, setAchData] = useState({ bankName: '', routingNumber: '', accountNumber: '', accountType: 'checking' });
  // ⚠️ Starts null and STAYS null on any error — the card entry must fail closed to the
  // incumbent fields. Only an explicit enabled config carrying a publishable key routes the
  // form to the provider; a failed config request must never blank the form a member is using.
  const [paymentConfig, setPaymentConfig] = useState(null);
  const [hasLegacyProfile, setHasLegacyProfile] = useState(false);

  // Billing address editing state
  const [billingEdits, setBillingEdits] = useState({}); // { cardId: { street, city, state, zip } }
  const [sameAsShipping, setSameAsShipping] = useState({}); // { cardId: boolean }
  const [savingBilling, setSavingBilling] = useState({}); // { cardId: boolean }

  // Members state
  const [members, setMembers] = useState([]);
  const [maxMembers, setMaxMembers] = useState(null);
  const [loadingMembers, setLoadingMembers] = useState(false);
  // Card 53577: a FAILED fetch leaves `maxMembers` at null, which is MORE permissive than any
  // real account state — the slot counter disappears while "Add Member" keeps rendering, so
  // the failure arm invites a dependent a 0-slot plan has no room for.
  const [membersLoadError, setMembersLoadError] = useState(false);
  const [showMemberForm, setShowMemberForm] = useState(false);
  const [memberFormData, setMemberFormData] = useState({ firstName: '', lastName: '', email: '', dateOfBirth: '', relationship: 'spouse' });
  const [savingMember, setSavingMember] = useState(false);

  // Account credit state
  const [creditBalanceCents, setCreditBalanceCents] = useState(0);
  const [loadingCredit, setLoadingCredit] = useState(false);
  const [creditError, setCreditError] = useState(false);
  const [redeemCode, setRedeemCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState('');
  const [redeemSuccess, setRedeemSuccess] = useState('');

  const tabs = [
    { id: 'membership', label: 'Membership', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2L2 7l10 5 10-5-10-5z"></path>
        <path d="M2 17l10 5 10-5"></path>
        <path d="M2 12l10 5 10-5"></path>
      </svg>
    )},
    { id: 'credit', label: 'Account Credit', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="5" width="20" height="14" rx="2"></rect>
        <path d="M2 10h20"></path>
      </svg>
    )},
    { id: 'profile', label: 'Profile Information', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
        <circle cx="12" cy="7" r="4"></circle>
      </svg>
    )},
    { id: 'documents', label: 'ID & Payment', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="4" width="20" height="16" rx="2"></rect>
        <path d="M6 12h4"></path>
        <path d="M14 12h4"></path>
        <path d="M6 16h12"></path>
      </svg>
    )},
    { id: 'password', label: 'Update Password', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
      </svg>
    )},
    { id: 'members', label: 'Members', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
        <circle cx="9" cy="7" r="4"></circle>
        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
      </svg>
    )},
    { id: 'privacy', label: 'Privacy Choices', icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
      </svg>
    )}
  ];

  // Fetch ID verification + payment methods when documents tab is active
  // Fetch members when members tab is active
  useEffect(() => {
    if (activeTab === 'membership') {
      fetchMembership();
    }
    if (activeTab === 'documents') {
      fetchIdVerification();
      fetchPaymentMethods();
      fetchPaymentConfig();
    }
    if (activeTab === 'members') {
      fetchMembers();
    }
    if (activeTab === 'credit') {
      fetchCreditBalance();
    }
  }, [activeTab]);

  const fetchCreditBalance = async () => {
    setLoadingCredit(true);
    try {
      const res = await api.get('/credits/balance');
      setCreditBalanceCents(res.balanceCents ?? 0);
      setCreditError(false);
    } catch {
      // Card 53601: ⛔ do NOT write a zero here. A failed read is not a zero
      // balance, and rendering $0.00 tells a member holding real credit that they
      // hold none. #53577 repaired the other three loaders in this file; this one
      // was outside its declared scope.
      setCreditError(true);
    } finally {
      setLoadingCredit(false);
    }
  };

  const handleRedeemCode = async (e) => {
    e.preventDefault();
    const code = redeemCode.trim();
    if (!code) {
      setRedeemError('Please enter a gift card code');
      setRedeemSuccess('');
      return;
    }
    setRedeeming(true);
    setRedeemError('');
    setRedeemSuccess('');
    try {
      const res = await api.post('/credits/redeem', { code });
      setCreditBalanceCents(res.balanceCents ?? 0);
      setRedeemSuccess(`${formatPrice(res.amountCreditedCents)} added to your account credit`);
      setRedeemCode('');
    } catch (error) {
      setRedeemError(error?.message || 'Unable to redeem this code');
    } finally {
      setRedeeming(false);
    }
  };

  const fetchIdVerification = async () => {
    setLoadingId(true);
    setIdLoadError(false);
    try {
      const res = await api.get('/users/me/id-verification');
      setIdVerification(res.idVerification || null);
    } catch {
      // Card 53577: surface the refusal. A null `idVerification` means "none on file";
      // it must never also mean "we could not ask".
      setIdVerification(null);
      setIdLoadError(true);
    } finally {
      setLoadingId(false);
    }
  };

  const fetchPaymentMethods = async () => {
    setLoadingPayments(true);
    setPaymentsLoadError(false);
    try {
      const res = await api.get('/users/me/payment-methods');
      setPaymentMethods({ cards: res.cards || [], ach: res.ach || null });
      // Card 52887: only the API knows whether this member still has an Authorize.Net profile;
      // it drives the one-time re-entry prompt. Absent (switch off) reads as false.
      setHasLegacyProfile(Boolean(res.hasLegacyProfile));
    } catch {
      // Card 53577: surface the refusal rather than rendering an empty wallet.
      setPaymentMethods({ cards: [], ach: null });
      setPaymentsLoadError(true);
    } finally {
      setLoadingPayments(false);
    }
  };

  // Card 52887: which provider owns card entry. Read once per mount, never re-decided here —
  // the server owns the switch, and a second copy of that test is how the two drift apart.
  const fetchPaymentConfig = async () => {
    try {
      const res = await api.get('/users/me/payment-method/config');
      setPaymentConfig(res || null);
    } catch {
      // Fails closed to the incumbent fields. A 404 is the switch being off, by design.
      setPaymentConfig(null);
    }
  };

  const handleIdUpload = (side, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    // Show preview and store pending file
    const previewUrl = URL.createObjectURL(file);
    if (side === 'front') {
      setFrontPreview(previewUrl);
      setPendingFront(file);
    } else {
      setBackPreview(previewUrl);
      setPendingBack(file);
    }
  };

  const handleSaveId = async () => {
    if (!pendingFront && !pendingBack) return;

    setSavingId(true);
    try {
      const formData = new FormData();
      if (pendingFront) formData.append('licenseFront', pendingFront);
      if (pendingBack) formData.append('licenseBack', pendingBack);

      const res = await fetch('/api/users/me/id-verification', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || data.message || 'Upload failed');

      setIdVerification(data.idVerification);
      setPendingFront(null);
      setPendingBack(null);
      toast.success('ID verification saved');
    } catch (error) {
      toast.error(error.message || 'Failed to save ID');
    } finally {
      setSavingId(false);
    }
  };

  const handleDeleteId = async () => {
    if (!window.confirm('Are you sure you want to remove your ID on file?')) return;
    try {
      await api.delete('/users/me/id-verification');
      setIdVerification(null);
      setFrontPreview(null);
      setBackPreview(null);
      toast.success('ID verification removed');
    } catch (error) {
      toast.error(error.message || 'Failed to remove ID');
    }
  };

  // Drag and drop handlers
  const makeDragHandlers = useCallback((side) => {
    const setDragOver = side === 'front' ? setDragOverFront : setDragOverBack;
    return {
      onDragOver: (e) => { e.preventDefault(); e.stopPropagation(); },
      onDragEnter: (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); },
      onDragLeave: (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); },
      onDrop: (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file && file.type.startsWith('image/')) {
          handleIdUpload(side, file);
        } else {
          toast.error('Please drop an image file');
        }
      }
    };
  }, []);

  // Payment methods
  const formatCardNumber = (val) => {
    const clean = val.replace(/\D/g, '').slice(0, 16);
    return clean.replace(/(\d{4})(?=\d)/g, '$1 ');
  };

  const formatExpiration = (val) => {
    const clean = val.replace(/\D/g, '').slice(0, 4);
    if (clean.length >= 3) return clean.slice(0, 2) + '/' + clean.slice(2);
    return clean;
  };

  const handleAddPayment = async () => {
    setSavingPayment(true);
    try {
      if (paymentType === 'card') {
        const clean = cardData.number.replace(/\s/g, '');
        if (clean.length < 13) { toast.error('Invalid card number'); setSavingPayment(false); return; }
        if (!cardData.expiration || cardData.expiration.length < 4) { toast.error('Invalid expiration'); setSavingPayment(false); return; }
        if (!cardData.cvv || cardData.cvv.length < 3) { toast.error('Invalid CVV'); setSavingPayment(false); return; }

        // Determine billing address - use profile if same as shipping, otherwise use manual input
        const billingAddress = sameAsShippingNew 
          ? { street: profileData.address || '', city: profileData.city || '', state: profileData.state || '', zip: profileData.zip || '' }
          : newBillingAddress;

        await api.post('/users/me/payment-method', {
          cardNumber: clean,
          expirationDate: cardData.expiration,
          cvv: cardData.cvv,
          nameOnCard: cardData.nameOnCard,
          billingAddress,
          // Card #54419 (D9). The API now REFUSES a card saved with no recorded mandate
          // (`400 PAYMENT_CONSENT_REQUIRED`), because three paths charge a stored card with
          // the member absent. These values mirror the disclosure rendered above the Save
          // button and the same object the provider Element already posts
          // (`StripePaymentElement.jsx`), so both card-entry arms record one vocabulary.
          consent: {
            savedForFutureUse: true,
            offSessionChargesAuthorized: true,
            frequency: 'unscheduled',
            amountDescription: 'Membership dues, order totals and outstanding balances as incurred',
            agreedAt: new Date().toISOString()
          }
        });
        toast.success('Card added successfully');
      } else {
        if (!achData.bankName) { toast.error('Bank name is required'); setSavingPayment(false); return; }
        if (achData.routingNumber.length !== 9) { toast.error('Routing number must be 9 digits'); setSavingPayment(false); return; }
        if (!achData.accountNumber) { toast.error('Account number is required'); setSavingPayment(false); return; }

        await api.post('/users/me/ach-method', achData);
        toast.success('Bank account added successfully');
      }

      setShowPaymentForm(false);
      setCardData({ number: '', expiration: '', cvv: '', nameOnCard: '' });
      setAchData({ bankName: '', routingNumber: '', accountNumber: '', accountType: 'checking' });
      setSameAsShippingNew(true);
      setNewBillingAddress({ street: '', city: '', state: '', zip: '' });
      fetchPaymentMethods();
    } catch (error) {
      toast.error(error.message || 'Failed to add payment method');
    } finally {
      setSavingPayment(false);
    }
  };

  // Card 52887: the provider's Element has already confirmed the SetupIntent and the API has
  // already recorded the token by the time this runs — there is nothing left to post, which is
  // the entire point. Re-reading the list is what proves the card survived the round trip.
  const handleProviderCardSaved = () => {
    toast.success('Card added successfully');
    setShowPaymentForm(false);
    fetchPaymentMethods();
  };

  const handleDeletePayment = async (id, type) => {
    if (!window.confirm('Remove this payment method?')) return;
    try {
      await api.delete(`/users/me/payment-method/${id}?type=${type}`);
      toast.success('Payment method removed');
      fetchPaymentMethods();
    } catch (error) {
      toast.error(error.message || 'Failed to remove payment method');
    }
  };
  // Billing address editing handlers
  const toggleBillingEdit = (cardId) => {
    setBillingEdits(prev => {
      if (prev[cardId]) {
        const updated = { ...prev };
        delete updated[cardId];
        return updated;
      }
      // Initialize with existing billing address or empty
      const card = paymentMethods.cards.find(c => c.id === cardId);
      return {
        ...prev,
        [cardId]: card?.billingAddress || { street: "", city: "", state: "", zip: "" }
      };
    });
  };

  const handleBillingChange = (cardId, field, value) => {
    setBillingEdits(prev => ({
      ...prev,
      [cardId]: { ...prev[cardId], [field]: value }
    }));
  };

  const handleSameAsShipping = (cardId, checked) => {
    setSameAsShipping(prev => ({ ...prev, [cardId]: checked }));
    if (checked) {
      setBillingEdits(prev => ({
        ...prev,
        [cardId]: {
          street: profileData.address || "",
          city: profileData.city || "",
          state: profileData.state || "",
          zip: profileData.zip || ""
        }
      }));
    }
  };

  const handleSaveBilling = async (cardId) => {
    const billing = billingEdits[cardId];
    if (!billing) return;

    setSavingBilling(prev => ({ ...prev, [cardId]: true }));
    try {
      await api.patch(`/users/me/payment-method/${cardId}/billing-address`, { billingAddress: billing });
      toast.success("Billing address saved");
      fetchPaymentMethods();
      setBillingEdits(prev => {
        const updated = { ...prev };
        delete updated[cardId];
        return updated;
      });
      setSameAsShipping(prev => ({ ...prev, [cardId]: false }));
    } catch (error) {
      toast.error(error.message || "Failed to save billing address");
    } finally {
      setSavingBilling(prev => ({ ...prev, [cardId]: false }));
    }
  };

  // ============================================
  // MEMBER FUNCTIONS
  // ============================================

  const fetchMembership = async () => {
    setLoadingMembership(true);
    try {
      const res = await api.get('/users/membership');
      setMembershipData(res);
    } catch {
      toast.error('Failed to load membership data');
    } finally {
      setLoadingMembership(false);
    }
  };

  const handleUpgrade = () => {
    if (window.Intercom) {
      window.Intercom('showNewMessage', 'I am looking to upgrade my plan');
    } else {
      // Only the API branch needs the latch -- re-opening the Intercom widget
      // is idempotent, while each POST files a separate support ticket.
      if (upgradeInFlight.current) return;
      upgradeInFlight.current = true;
      setRequestingUpgrade(true);
      api.post('/support/tickets', {
        subject: 'Plan Upgrade Request',
        message: 'I am looking to upgrade my plan',
        category: 'account'
      }).then(() => {
        toast.success('Upgrade request submitted! We\'ll be in touch soon.');
      }).catch(() => {
        toast.error('Failed to submit upgrade request. Please try again.');
      }).finally(() => {
        upgradeInFlight.current = false;
        setRequestingUpgrade(false);
      });
    }
  };

  const fetchMembers = async () => {
    setLoadingMembers(true);
    setMembersLoadError(false);
    try {
      const res = await api.get('/users/me/members');
      setMembers(res.members || []);
      setMaxMembers(res.maxMembers ?? null);
    } catch {
      // Card 53577: surface the refusal. `maxMembers` stays null here, which is why the
      // render must gate on this flag BEFORE the null-means-unlimited branch at the
      // "Add Member" control — otherwise a failed read is the most permissive state there is.
      setMembers([]);
      setMaxMembers(null);
      setMembersLoadError(true);
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleMemberFormChange = (e) => {
    const { name, value } = e.target;
    setMemberFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    setSavingMember(true);
    try {
      const res = await api.post('/users/me/members', memberFormData);
      setMembers(prev => [...prev, res.member]);
      setMemberFormData({ firstName: '', lastName: '', email: '', dateOfBirth: '', relationship: 'spouse' });
      setShowMemberForm(false);
      toast.success('Member added successfully');
    } catch (error) {
      toast.error(error.response?.data?.message || error.message || 'Failed to add member');
    } finally {
      setSavingMember(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    try {
      await api.delete(`/users/me/members/${memberId}`);
      setMembers(prev => prev.filter(m => m.id !== memberId));
      toast.success('Member removed');
    } catch (error) {
      toast.error(error.message || 'Failed to remove member');
    }
  };

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfileData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };



  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const addressData = buildAddressPayload(profileData);

      const response = await api.patch('/users/profile', {
        ...(profileData.firstName && { firstName: profileData.firstName }),
        ...(profileData.lastName && { lastName: profileData.lastName }),
        ...(profileData.phone && { phone: profileData.phone }),
        ...(profileData.dateOfBirth && { dateOfBirth: profileData.dateOfBirth }),
        ...(profileData.gender && { gender: profileData.gender }),
        ...(profileData.weight && { weight: profileData.weight }),
        ...(profileData.height && { height: profileData.height }),
        ...(addressData && { address: addressData })
      });
      updateUser(response.user);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setSaving(true);
    try {
      await api.post('/auth/change-password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      });
      toast.success('Password updated. Please log in with your new password.');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      // Log out after password change so they re-authenticate with new password
      setTimeout(() => { logout(); }, 1500);
    } catch (error) {
      toast.error(error.message || 'Failed to update password');
    } finally {
      setSaving(false);
    }
  };

  // Render a drop zone for ID upload
  const renderDropZone = (side, isDragOver, inputRef, existingUrl) => {
    const preview = side === 'front' ? frontPreview : backPreview;
    const imgSrc = preview || existingUrl;
    const dragHandlers = makeDragHandlers(side);
    const label = side === 'front' ? 'Front' : 'Back';

    return (
      <div className={styles.idImageCard}>
        <span className={styles.idImageLabel}>{label}</span>
        <div
          className={`${styles.idDropZone} ${isDragOver ? styles.idDropZoneActive : ''}`}
          {...dragHandlers}
          onClick={() => inputRef.current?.click()}
        >
          {imgSrc ? (
            <img loading="lazy" src={imgSrc} alt={`ID ${label}`} className={styles.idDropZoneImg} />
          ) : (
            <div className={styles.idDropZonePlaceholder}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              <span>{isDragOver ? 'Drop to upload' : `Drag & drop or click to upload`}</span>
            </div>
          )}
          {isDragOver && imgSrc && (
            <div className={styles.idDropOverlay}>Drop to replace</div>
          )}
        </div>
        <input
          type="file"
          accept="image/*,.pdf"
          ref={inputRef}
          style={{ display: 'none' }}
          onChange={(e) => handleIdUpload(side, e.target.files?.[0])}
        />
        {imgSrc && (
          <button
            className={styles.idReplaceBtn}
            onClick={(e) => { e.stopPropagation(); inputRef.current?.click(); }}
            disabled={uploadingId}
          >
            Replace
          </button>
        )}
      </div>
    );
  };

  const brandLabels = { visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex', discover: 'Discover', unknown: 'Card' };

  // Card 52887 — the three derived answers the payment tab needs.
  //
  // `isProviderCardEntry` is deliberately conjunctive on the KEY as well as the flag: an
  // enabled-but-unconfigured deployment has `publishableKey: null`, and mounting an Element
  // without one renders a permanently broken box instead of falling back to fields that work.
  // It also tests `paymentType`, so switching to ACH keeps the incumbent ACH form and its own
  // save button.
  const isProviderCardEntry = Boolean(
    paymentConfig?.enabled && paymentConfig?.publishableKey && paymentType === 'card'
  );
  const hasProviderMethod = paymentMethods.cards.some((c) => c.provider === 'stripe');
  // "One time" is structural, not a stored flag: the prompt is shown to a member who has a
  // legacy profile and no provider method yet, so adding one is what retires it.
  const showLegacyReentryPrompt = Boolean(
    paymentConfig?.enabled && hasLegacyProfile && !hasProviderMethod
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>My Profile</h1>
        <p>Manage your account settings and preferences</p>
      </div>

      <div className={styles.layout}>
        {/* Sidebar */}
        <div className={styles.sidebar}>
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`${styles.tabBtn} ${activeTab === tab.id ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className={styles.tabIcon}>{tab.icon}</span>
              <span className={styles.tabLabel}>{tab.label}</span>
              <svg className={styles.tabArrow} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </button>
          ))}

          <div className={styles.membershipCard}>
            <h3>Manage your membership</h3>
            <button className={styles.contactBtn} onClick={() => navigate('/support')}>Open Ticket</button>
          </div>


        </div>

        {/* Content */}
        <div className={styles.content}>
          {activeTab === 'membership' && (
            <div className={styles.section}>
              {loadingMembership ? (
                <div className={styles.loading}>Loading membership info...</div>
              ) : membershipData ? (
                <>
                  <div className={styles.membershipPlanHeader}>
                    <h2 className={styles.planName}>{membershipData.planName}</h2>
                    <p className={styles.memberSince}>
                      Member since {formatDate(membershipData.memberSince, { year: 'numeric', month: 'long' })}
                    </p>
                    {membershipData.canUpgrade && (
                      <button
                        className={styles.upgradeBtn}
                        onClick={handleUpgrade}
                        disabled={requestingUpgrade}
                      >
                        {requestingUpgrade ? 'Submitting...' : 'Upgrade Plan'}
                      </button>
                    )}
                  </div>

                  {membershipData.features?.length > 0 && (
                    <div className={styles.featuresSection}>
                      <h3 className={styles.featuresSectionTitle}>Your Plan Features</h3>
                      <ul className={styles.featuresList}>
                        {membershipData.features.map((feature, idx) => (
                          <li key={idx} className={styles.featureItem}>
                            <svg className={styles.featureCheck} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="3">
                              <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {membershipData.savings?.totalSavings > 0 ? (
                    <div className={styles.savingsCard}>
                      <h3 className={styles.savingsTotal}>
                        You have saved {formatPrice(membershipData.savings.totalSavings)} with Oval
                      </h3>
                      <div className={styles.savingsBreakdown}>
                        {membershipData.savings.retailSavings > 0 && (
                          <div className={styles.savingsRow}>
                            <span>Membership pricing</span>
                            <span>{formatPrice(membershipData.savings.retailSavings)}</span>
                          </div>
                        )}
                        {membershipData.savings.consultationSavings > 0 && (
                          <div className={styles.savingsRow}>
                            <span>Free consultations</span>
                            <span>{formatPrice(membershipData.savings.consultationSavings)}</span>
                          </div>
                        )}
                        {membershipData.savings.shippingSavings > 0 && (
                          <div className={styles.savingsRow}>
                            <span>Free shipping</span>
                            <span>{formatPrice(membershipData.savings.shippingSavings)}</span>
                          </div>
                        )}
                        {membershipData.savings.promoSavings > 0 && (
                          <div className={styles.savingsRow}>
                            <span>Promo codes</span>
                            <span>{formatPrice(membershipData.savings.promoSavings)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className={styles.noSavings}>
                      <p>Start shopping to see your savings grow!</p>
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.emptyState}>
                  <p>Unable to load membership information</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'credit' && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2>Account Credit</h2>
                <p>Redeem a gift card and apply your balance at checkout</p>
              </div>

              {creditError ? (
                <div className={styles.emptyState} role="alert">
                  <p>Could not load your credit balance.</p>
                  <button className={styles.addBtn} onClick={fetchCreditBalance}>Try again</button>
                </div>
              ) : (
                <div className={styles.creditBalanceCard}>
                  <span className={styles.creditBalanceLabel}>Available Credit</span>
                  <span className={styles.creditBalanceAmount}>
                    {loadingCredit ? '…' : formatPrice(creditBalanceCents)}
                  </span>
                </div>
              )}

              <form className={styles.redeemForm} onSubmit={handleRedeemCode}>
                <div className={styles.formGroupFull}>
                  <label>Gift Card Code</label>
                  <input
                    type="text"
                    value={redeemCode}
                    onChange={(e) => {
                      setRedeemCode(e.target.value);
                      if (redeemError) setRedeemError('');
                      if (redeemSuccess) setRedeemSuccess('');
                    }}
                    placeholder="e.g., GIFT-XXXXXXXX"
                    autoComplete="off"
                    disabled={redeeming}
                  />
                </div>

                {redeemError && <div className={styles.redeemError}>{redeemError}</div>}
                {redeemSuccess && <div className={styles.redeemSuccess}>{redeemSuccess}</div>}

                <button type="submit" className={styles.saveBtn} disabled={redeeming || isDemo}>
                  {redeeming ? 'Redeeming...' : 'Redeem'}
                </button>
              </form>
            </div>
          )}

          {activeTab === 'profile' && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2>Personal Information</h2>
                <p>Used for shipping, prescriptions, and care coordination</p>
              </div>

              <div className={styles.formGrid}>
                <div className={styles.formGroup}>
                  <label>First Name <span className={styles.required}>*</span></label>
                  <input type="text" name="firstName" value={profileData.firstName} onChange={handleProfileChange} placeholder="Enter first name" />
                </div>
                <div className={styles.formGroup}>
                  <label>Last Name <span className={styles.required}>*</span></label>
                  <input type="text" name="lastName" value={profileData.lastName} onChange={handleProfileChange} placeholder="Enter last name" />
                </div>
                <div className={styles.formGroup}>
                  <label>Email <span className={styles.required}>*</span></label>
                  <input type="email" name="email" value={profileData.email} disabled className={styles.disabled} />
                </div>
                <div className={styles.formGroup}>
                  <label>Birth date <span className={styles.required}>*</span></label>
                  <input type="date" name="dateOfBirth" value={profileData.dateOfBirth} onChange={handleProfileChange} />
                </div>
                <div className={styles.formGroup}>
                  <label>Phone <span className={styles.required}>*</span></label>
                  <input type="tel" name="phone" value={profileData.phone} onChange={handleProfileChange} placeholder="Enter phone number" />
                </div>
                <div className={styles.formGroup}>
                  <label>Gender <span className={styles.required}>*</span></label>
                  <select name="gender" value={profileData.gender} onChange={handleProfileChange}>
                    <option value="">Select gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                    <option value="prefer_not_to_say">Prefer not to say</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>Weight (lbs) <span className={styles.required}>*</span></label>
                  <input type="text" name="weight" value={profileData.weight} onChange={handleProfileChange} placeholder="e.g., 180" />
                </div>
                <div className={styles.formGroup}>
                  <label>Height (ft / in) <span className={styles.required}>*</span></label>
                  <input type="text" name="height" value={profileData.height} onChange={handleProfileChange} placeholder="e.g., 5'10" />
                </div>
                <div className={styles.formGroupFull}>
                  <label>Street Address <span className={styles.required}>*</span></label>
                  <AddressAutocomplete name="address" value={profileData.address} onChange={(val) => setProfileData(prev => ({ ...prev, address: val }))} onSelect={({ street, city, state, zipCode }) => setProfileData(prev => ({ ...prev, address: street || prev.address, city: city || prev.city, state: state || prev.state, zip: zipCode || prev.zip }))} placeholder="Start typing your address..." label={null} />
                </div>
              </div>

              <div className={styles.addressRow}>
                <div className={styles.formGroup}>
                  <label>State <span className={styles.required}>*</span></label>
                  <input type="text" name="state" value={profileData.state} onChange={handleProfileChange} placeholder="e.g., GA" />
                </div>
                <div className={styles.formGroup}>
                  <label>City <span className={styles.required}>*</span></label>
                  <input type="text" name="city" value={profileData.city} onChange={handleProfileChange} placeholder="e.g., Warner Robins" />
                </div>
                <div className={styles.formGroup}>
                  <label>ZIP <span className={styles.required}>*</span></label>
                  <input type="text" name="zip" value={profileData.zip} onChange={handleProfileChange} placeholder="e.g., 31088-9168" />
                </div>
              </div>

              <button onClick={handleSaveProfile} className={styles.saveBtn} disabled={saving || isDemo}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className={styles.section}>
              {/* ID Verification Section */}
              <div className={styles.sectionHeader}>
                <h2>ID Verification</h2>
                <p>Your government-issued ID used for prescription verification</p>
              </div>

              {loadingId ? (
                <div className={styles.emptyState}><p>Loading...</p></div>
              ) : idLoadError ? (
                /* Card 53577: the refusal branch. Ordered BEFORE the `idVerification` test so a
                   failed read can never fall through to the upload prompt — offering the drop
                   zones here is what invites a member to re-upload an ID we already hold. */
                <div className={styles.emptyState} role="alert">
                  <p>Could not load your ID verification.</p>
                  <button className={styles.addBtn} onClick={fetchIdVerification}>Try again</button>
                </div>
              ) : idVerification ? (
                <div className={styles.idOnFile}>
                  <div className={styles.idStatusBar}>
                    <span className={styles.idBadge}>✓ ID on file</span>
                    <span className={styles.idDate}>
                      Uploaded {new Date(idVerification.uploadedAt).toLocaleDateString()}
                      {idVerification.source === 'intake' ? ' (via intake form)' : ''}
                    </span>
                  </div>
                  <div className={styles.idImages}>
                    {renderDropZone('front', dragOverFront, frontInputRef, idVerification.frontUrl)}
                    {renderDropZone('back', dragOverBack, backInputRef, idVerification.backUrl)}
                  </div>
                  <button className={styles.idDeleteBtn} onClick={handleDeleteId}>Remove ID</button>
                  {(pendingFront || pendingBack) && (
                    <button 
                      className={styles.saveBtn} 
                      onClick={handleSaveId} 
                      disabled={savingId || isDemo}
                      style={{ marginTop: '0.5rem' }}
                    >
                      {savingId ? 'Saving...' : 'Save ID'}
                    </button>
                  )}
                </div>
              ) : (
                <div className={styles.idUploadEmpty}>
                  <p style={{ textAlign: 'center', color: '#737373', marginBottom: '1rem' }}>
                    No ID on file. Upload your government-issued ID for faster checkout.
                  </p>
                  <div className={styles.idImages}>
                    {renderDropZone('front', dragOverFront, frontInputRef, null)}
                    {renderDropZone('back', dragOverBack, backInputRef, null)}
                  </div>
                  <button 
                    className={styles.saveBtn} 
                    onClick={handleSaveId} 
                    disabled={(!pendingFront && !pendingBack) || savingId || isDemo}
                    style={{ marginTop: '1rem' }}
                  >
                    {savingId ? 'Saving...' : 'Save ID'}
                  </button>
                </div>
              )}

              {/* Payment Methods Section */}
              <div className={styles.sectionHeader} style={{ marginTop: '2.5rem' }}>
                <h2>Payment Methods</h2>
                <p>Cards and bank accounts on file</p>
              </div>

              {loadingPayments ? (
                <div className={styles.emptyState}><p>Loading...</p></div>
              ) : paymentsLoadError ? (
                /* Card 53577: the refusal branch. It replaces the WHOLE section body, so the
                   "+ Add Payment Method" control is not offered either — a member who cannot
                   see the card already on file is exactly the member who would add it twice. */
                <div className={styles.emptyState} role="alert">
                  <p>Could not load your payment methods.</p>
                  <button className={styles.addBtn} onClick={fetchPaymentMethods}>Try again</button>
                </div>
              ) : (
                <>
                  {/* Saved cards */}
                  {paymentMethods.cards.map(card => (
                    <div key={card.id} className={styles.paymentCard} style={{ flexDirection: "column", alignItems: "stretch" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div className={styles.paymentCardInfo}>
                          <div className={styles.paymentCardIcon}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                              <line x1="1" y1="10" x2="23" y2="10"></line>
                            </svg>
                          </div>
                          <div>
                            <div className={styles.paymentCardTitle}>
                              {brandLabels[card.cardBrand] || "Card"} •••• {card.cardLast4}
                            </div>
                            <div className={styles.paymentCardSub}>
                              Expires {card.expirationDate}{card.nameOnCard ? ` · ${card.nameOnCard}` : ""}
                            </div>
                            {card.billingAddress && !billingEdits[card.id] && (
                              <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: "2px" }}>
                                {card.billingAddress.street}{card.billingAddress.city ? `, ${card.billingAddress.city}` : ""}{card.billingAddress.state ? `, ${card.billingAddress.state}` : ""}{card.billingAddress.zip ? ` ${card.billingAddress.zip}` : ""}
                              </div>
                            )}
                          </div>
                        </div>
                        {/*
                          Card 52887: both controls address a LEGACY `card_payment_methods` row —
                          the billing PATCH and the delete both key on that table — so neither can
                          act on a provider-tokenized method, which lives in `payment_methods`.
                          Hiding them for a provider card avoids shipping a button that silently
                          does nothing. Provider-side removal belongs with the methods lifecycle,
                          not with this card's PAN-reduction scope; a provider row can only exist
                          once the switch is on, so no member loses a control they have today.
                        */}
                        {card.provider !== 'stripe' && (
                          <div style={{ display: "flex", gap: "0.5rem" }}>
                            <button className={styles.paymentRemoveBtn} onClick={() => toggleBillingEdit(card.id)} style={{ color: "#6b7280" }}>
                              {billingEdits[card.id] ? "Cancel" : (card.billingAddress ? "Edit Billing" : "Add Billing")}
                            </button>
                            <button className={styles.paymentRemoveBtn} onClick={() => handleDeletePayment(card.id, "card")}>Remove</button>
                          </div>
                        )}
                      </div>

                      {billingEdits[card.id] && (
                        <div style={{ marginTop: "1rem", paddingTop: "1rem", borderTop: "1px solid #f3e5d0" }}>
                          <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem", cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              checked={sameAsShipping[card.id] || false}
                              onChange={(e) => handleSameAsShipping(card.id, e.target.checked)}
                              style={{ width: "16px", height: "16px" }}
                            />
                            <span style={{ fontSize: "0.875rem" }}>Billing address same as shipping address</span>
                          </label>
                          <div className={styles.formGridSingle}>
                            <div className={styles.formGroup}>
                              <label>Street Address</label>
                              <input
                                type="text"
                                value={billingEdits[card.id]?.street || ""}
                                onChange={(e) => handleBillingChange(card.id, "street", e.target.value)}
                                placeholder="123 Main St"
                                disabled={sameAsShipping[card.id]}
                                style={sameAsShipping[card.id] ? { backgroundColor: "#f5f5f5", cursor: "not-allowed" } : {}}
                              />
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "1rem" }}>
                              <div className={styles.formGroup}>
                                <label>City</label>
                                <input
                                  type="text"
                                  value={billingEdits[card.id]?.city || ""}
                                  onChange={(e) => handleBillingChange(card.id, "city", e.target.value)}
                                  placeholder="City"
                                  disabled={sameAsShipping[card.id]}
                                  style={sameAsShipping[card.id] ? { backgroundColor: "#f5f5f5", cursor: "not-allowed" } : {}}
                                />
                              </div>
                              <div className={styles.formGroup}>
                                <label>State</label>
                                <input
                                  type="text"
                                  value={billingEdits[card.id]?.state || ""}
                                  onChange={(e) => handleBillingChange(card.id, "state", e.target.value)}
                                  placeholder="CA"
                                  maxLength={2}
                                  disabled={sameAsShipping[card.id]}
                                  style={sameAsShipping[card.id] ? { backgroundColor: "#f5f5f5", cursor: "not-allowed" } : {}}
                                />
                              </div>
                              <div className={styles.formGroup}>
                                <label>ZIP</label>
                                <input
                                  type="text"
                                  value={billingEdits[card.id]?.zip || ""}
                                  onChange={(e) => handleBillingChange(card.id, "zip", e.target.value)}
                                  placeholder="12345"
                                  maxLength={10}
                                  disabled={sameAsShipping[card.id]}
                                  style={sameAsShipping[card.id] ? { backgroundColor: "#f5f5f5", cursor: "not-allowed" } : {}}
                                />
                              </div>
                            </div>
                          </div>
                          <button
                            className={styles.saveBtn}
                            onClick={() => handleSaveBilling(card.id)}
                            disabled={savingBilling[card.id] || isDemo}
                            style={{ marginTop: "0.75rem" }}
                          >
                            {savingBilling[card.id] ? "Saving..." : "Save Billing Address"}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                  {/* Saved ACH */}
                  {paymentMethods.ach && (
                    <div className={styles.paymentCard}>
                      <div className={styles.paymentCardInfo}>
                        <div className={styles.paymentCardIcon}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M3 21h18"></path>
                            <path d="M3 10h18"></path>
                            <path d="M5 6l7-3 7 3"></path>
                            <path d="M4 10v11"></path>
                            <path d="M20 10v11"></path>
                            <path d="M8 14v3"></path>
                            <path d="M12 14v3"></path>
                            <path d="M16 14v3"></path>
                          </svg>
                        </div>
                        <div>
                          <div className={styles.paymentCardTitle}>
                            {paymentMethods.ach.bankName} •••• {paymentMethods.ach.accountNumberLast4}
                          </div>
                          <div className={styles.paymentCardSub}>
                            {paymentMethods.ach.accountType?.charAt(0).toUpperCase() + paymentMethods.ach.accountType?.slice(1)} account
                          </div>
                        </div>
                      </div>
                      <button className={styles.paymentRemoveBtn} onClick={() => handleDeletePayment(paymentMethods.ach.id, 'ach')}>Remove</button>
                    </div>
                  )}

                  {/* Empty state */}
                  {paymentMethods.cards.length === 0 && !paymentMethods.ach && !showPaymentForm && (
                    <div className={styles.emptyState}>
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ margin: '0 auto 1rem', display: 'block', color: '#d4b896' }}>
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                      <p>No payment methods saved yet.</p>
                    </div>
                  )}

                  {/*
                    Card 52887: the one-time re-entry prompt. Shown only to a member who still
                    has an Authorize.Net profile and has not yet saved a provider method — the
                    incumbent profile is left exactly where it is (#52881), so this asks rather
                    than migrates. Styling uses the global tokens (0.5px border, tan palette)
                    per STANDARDS.md; inline style matches the idiom already used throughout
                    this file rather than adding a class to a stylesheet this card does not own.
                  */}
                  {showLegacyReentryPrompt && (
                    <div
                      role="status"
                      style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        border: '0.5px solid var(--color-tan-300)',
                        borderRadius: '12px',
                        background: 'var(--color-tan-100)'
                      }}
                    >
                      <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-charcoal)' }}>
                        Please re-enter your card
                      </p>
                      <p style={{ margin: '0.375rem 0 0', fontSize: '0.875rem', lineHeight: 1.5, color: 'var(--color-gray-500)' }}>
                        Your saved card is held with our previous payment processor. Add it once more to
                        move it to our new secure card entry — we will only ask you this one time.
                      </p>
                    </div>
                  )}

                  {/* Add Payment Method Form */}
                  {showPaymentForm ? (
                    <div className={styles.paymentForm}>
                      <div className={styles.paymentToggle}>
                        <button
                          className={`${styles.paymentToggleBtn} ${paymentType === 'card' ? styles.paymentToggleActive : ''}`}
                          onClick={() => setPaymentType('card')}
                        >
                          Credit Card
                        </button>
                        <button
                          className={`${styles.paymentToggleBtn} ${paymentType === 'ach' ? styles.paymentToggleActive : ''}`}
                          onClick={() => setPaymentType('ach')}
                        >
                          Bank Account (ACH)
                        </button>
                      </div>

                      {paymentType === 'card' ? (
                        /*
                          Card 52887 — the fork this whole card exists for. ON: the provider's
                          iframe owns the PAN, expiry and CVC and this origin never sees them.
                          OFF: the incumbent fields below, byte-for-byte as they were.
                          ⛔ DO NOT re-wrap the Element's fields in Oval inputs and ⛔ do not
                          re-validate a PAN here — reading the number back out, even to
                          length-check it, puts it in this document and undoes the reduction.
                        */
                        isProviderCardEntry ? (
                          <Suspense fallback={<p className={styles.paymentCardSub}>Preparing secure card entry...</p>}>
                            <StripePaymentElement
                              publishableKey={paymentConfig.publishableKey}
                              onSaved={handleProviderCardSaved}
                              onCancel={() => setShowPaymentForm(false)}
                            />
                          </Suspense>
                        ) : (
                        <div className={styles.formGridSingle}>
                          <div className={styles.formGroup}>
                            <label>Card Number</label>
                            <input
                              type="text"
                              placeholder="1234 5678 9012 3456"
                              value={cardData.number}
                              onChange={(e) => setCardData(p => ({ ...p, number: formatCardNumber(e.target.value) }))}
                              maxLength={19}
                            />
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <div className={styles.formGroup}>
                              <label>Expiration</label>
                              <input
                                type="text"
                                placeholder="MM/YY"
                                value={cardData.expiration}
                                onChange={(e) => setCardData(p => ({ ...p, expiration: formatExpiration(e.target.value) }))}
                                maxLength={5}
                              />
                            </div>
                            <div className={styles.formGroup}>
                              <label>CVV</label>
                              <input
                                type="text"
                                placeholder="123"
                                value={cardData.cvv}
                                onChange={(e) => setCardData(p => ({ ...p, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                                maxLength={4}
                              />
                            </div>
                          </div>
                          <div className={styles.formGroup}>
                            <label>Name on Card</label>
                            <input
                              type="text"
                              placeholder="John Doe"
                              value={cardData.nameOnCard}
                              onChange={(e) => setCardData(p => ({ ...p, nameOnCard: e.target.value }))}
                            />
                          </div>

                          {/*
                            Card #54419 (D9). The disclosure the recorded mandate describes.
                            The POST below is refused without a consent record, so this text must
                            stay the member-visible half of it: the card is stored, it may be
                            charged later without the card being entered again, charges are
                            unscheduled, and the amount is whatever is owed at the time. Do not
                            reword the POST's `amountDescription` without rewording this.
                          */}
                          <p className={styles.paymentCardSub} style={{ margin: '0.75rem 0 0' }}>
                            By saving this card you authorize Oval Care to keep it on file and to charge it
                            for future orders, membership dues and any outstanding balance without asking
                            you to re-enter it. Charges are unscheduled and for the amount owed at the time.
                          </p>

                          {/* Billing Address */}
                          <div style={{ marginTop: "0.5rem", paddingTop: "1rem", borderTop: "1px solid #f3e5d0" }}>
                            <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.75rem", cursor: "pointer" }}>
                              <input
                                type="checkbox"
                                checked={sameAsShippingNew}
                                onChange={(e) => {
                                  setSameAsShippingNew(e.target.checked);
                                  if (e.target.checked) {
                                    setNewBillingAddress({ street: profileData.address || "", city: profileData.city || "", state: profileData.state || "", zip: profileData.zip || "" });
                                  }
                                }}
                                style={{ width: "16px", height: "16px" }}
                              />
                              <span style={{ fontSize: "0.875rem" }}>Same billing address as shipping address</span>
                            </label>
                            {!sameAsShippingNew && (
                              <>
                                <div className={styles.formGroup}>
                                  <label>Street Address</label>
                                  <input
                                    type="text"
                                    value={newBillingAddress.street}
                                    onChange={(e) => setNewBillingAddress(p => ({ ...p, street: e.target.value }))}
                                    placeholder="123 Main St"
                                  />
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "1rem" }}>
                                  <div className={styles.formGroup}>
                                    <label>City</label>
                                    <input
                                      type="text"
                                      value={newBillingAddress.city}
                                      onChange={(e) => setNewBillingAddress(p => ({ ...p, city: e.target.value }))}
                                      placeholder="City"
                                    />
                                  </div>
                                  <div className={styles.formGroup}>
                                    <label>State</label>
                                    <input
                                      type="text"
                                      value={newBillingAddress.state}
                                      onChange={(e) => setNewBillingAddress(p => ({ ...p, state: e.target.value }))}
                                      placeholder="CA"
                                      maxLength={2}
                                    />
                                  </div>
                                  <div className={styles.formGroup}>
                                    <label>ZIP</label>
                                    <input
                                      type="text"
                                      value={newBillingAddress.zip}
                                      onChange={(e) => setNewBillingAddress(p => ({ ...p, zip: e.target.value }))}
                                      placeholder="12345"
                                      maxLength={10}
                                    />
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        )
                      ) : (
                        <div className={styles.formGridSingle}>
                          <div className={styles.formGroup}>
                            <label>Bank Name</label>
                            <input
                              type="text"
                              placeholder="e.g., Chase, Wells Fargo"
                              value={achData.bankName}
                              onChange={(e) => setAchData(p => ({ ...p, bankName: e.target.value }))}
                            />
                          </div>
                          <div className={styles.formGroup}>
                            <label>Routing Number</label>
                            <input
                              type="text"
                              placeholder="9 digits"
                              value={achData.routingNumber}
                              onChange={(e) => setAchData(p => ({ ...p, routingNumber: e.target.value.replace(/\D/g, '').slice(0, 9) }))}
                              maxLength={9}
                            />
                          </div>
                          <div className={styles.formGroup}>
                            <label>Account Number</label>
                            <input
                              type="text"
                              placeholder="Account number"
                              value={achData.accountNumber}
                              onChange={(e) => setAchData(p => ({ ...p, accountNumber: e.target.value.replace(/\D/g, '') }))}
                            />
                          </div>
                          <div className={styles.formGroup}>
                            <label>Account Type</label>
                            <select
                              value={achData.accountType}
                              onChange={(e) => setAchData(p => ({ ...p, accountType: e.target.value }))}
                            >
                              <option value="checking">Checking</option>
                              <option value="savings">Savings</option>
                            </select>
                          </div>
                        </div>
                      )}

                      {/*
                        Card 52887: the Element brings its own Save and Cancel, because the
                        submit has to run INSIDE the provider's context to confirm the
                        SetupIntent. `handleAddPayment` is the incumbent POST and must not be
                        reachable while the Element is mounted — it reads `cardData`, which the
                        provider path never fills. The ACH form keeps this row, which is why the
                        test is `isProviderCardEntry` (card-only) and not just the switch.
                      */}
                      {!isProviderCardEntry && (
                        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                          <button className={styles.saveBtn} onClick={handleAddPayment} disabled={savingPayment || isDemo}>
                            {savingPayment ? 'Saving...' : 'Save Payment Method'}
                          </button>
                          <button
                            className={styles.idReplaceBtn}
                            onClick={() => setShowPaymentForm(false)}
                            style={{ padding: '0.875rem 1.5rem' }}
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      className={styles.addBtn}
                      onClick={() => setShowPaymentForm(true)}
                      style={{ marginTop: '1rem' }}
                    >
                      + Add Payment Method
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'password' && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2>Update Password</h2>
                <p>Change your password to keep your account secure</p>
              </div>
              <div className={styles.formGridSingle}>
                <div className={styles.formGroup}>
                  <label>Current Password</label>
                  <input type="password" name="currentPassword" value={passwordData.currentPassword} onChange={handlePasswordChange} placeholder="Enter current password" />
                </div>
                <div className={styles.formGroup}>
                  <label>New Password</label>
                  <input type="password" name="newPassword" value={passwordData.newPassword} onChange={handlePasswordChange} placeholder="Enter new password" />
                </div>
                <div className={styles.formGroup}>
                  <label>Confirm New Password</label>
                  <input type="password" name="confirmPassword" value={passwordData.confirmPassword} onChange={handlePasswordChange} placeholder="Confirm new password" />
                </div>
              </div>
              <button onClick={handleUpdatePassword} className={styles.saveBtn} disabled={saving || isDemo}>
                {saving ? 'Updating...' : 'Update Password'}
              </button>
            </div>
          )}

          {activeTab === 'members' && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2>Manage Members</h2>
                <p>Add family members or dependents to your account</p>
                {maxMembers !== null && maxMembers > 0 && (
                  <p className={styles.memberSlots}>{members.length} of {maxMembers} member slot{maxMembers !== 1 ? "s" : ""} used</p>
                )}
                {maxMembers === 0 && (
                  <p className={styles.memberSlots}>Your plan does not include additional members</p>
                )}
              </div>

              {loadingMembers ? (
                <div className={styles.loading}>Loading members...</div>
              ) : membersLoadError ? (
                /* Card 53577: the refusal branch, ordered BEFORE the list. It also suppresses
                   "Add Member" — on a failed read `maxMembers` is null, which the control below
                   treats as unlimited, so the failure arm was the most permissive state on the
                   page. A member must not be invited into a slot we could not count. */
                <div className={styles.emptyState} role="alert">
                  <p>Could not load your members.</p>
                  <button className={styles.addBtn} onClick={fetchMembers}>Try again</button>
                </div>
              ) : (
                <>
                  {/* Members List */}
                  {members.length > 0 && (
                    <div className={styles.membersList}>
                      {members.map(member => (
                        <div key={member.id} className={styles.memberCard}>
                          <div className={styles.memberInfo}>
                            <div className={styles.memberName}>{member.firstName} {member.lastName}</div>
                            <div className={styles.memberDetails}>
                              <span>{member.email}</span>
                              <span className={styles.memberRelationship}>{member.relationship}</span>
                            </div>
                          </div>
                          <button
                            className={styles.removeBtn}
                            onClick={() => handleRemoveMember(member.id)}
                            title="Remove member"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2"></path>
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add Member Form */}
                  {showMemberForm ? (
                    <form onSubmit={handleAddMember} className={styles.memberForm}>
                      <div className={styles.formGridDouble}>
                        <div className={styles.formGroup}>
                          <label>First Name *</label>
                          <input
                            type="text"
                            name="firstName"
                            value={memberFormData.firstName}
                            onChange={handleMemberFormChange}
                            required
                            placeholder="First name"
                          />
                        </div>
                        <div className={styles.formGroup}>
                          <label>Last Name *</label>
                          <input
                            type="text"
                            name="lastName"
                            value={memberFormData.lastName}
                            onChange={handleMemberFormChange}
                            required
                            placeholder="Last name"
                          />
                        </div>
                      </div>
                      <div className={styles.formGroup}>
                        <label>Email *</label>
                        <input
                          type="email"
                          name="email"
                          value={memberFormData.email}
                          onChange={handleMemberFormChange}
                          required
                          placeholder="member@example.com"
                        />
                      </div>
                      <div className={styles.formGridDouble}>
                        <div className={styles.formGroup}>
                          <label>Date of Birth *</label>
                          <input
                            type="date"
                            name="dateOfBirth"
                            value={memberFormData.dateOfBirth}
                            onChange={handleMemberFormChange}
                            required
                          />
                        </div>
                        <div className={styles.formGroup}>
                          <label>Relationship *</label>
                          <select
                            name="relationship"
                            value={memberFormData.relationship}
                            onChange={handleMemberFormChange}
                            required
                          >
                            <option value="spouse">Spouse</option>
                            <option value="child">Child</option>
                            <option value="other">Other</option>
                          </select>
                        </div>
                      </div>
                      <div className={styles.formActions}>
                        <button type="button" className={styles.cancelBtn} onClick={() => setShowMemberForm(false)}>
                          Cancel
                        </button>
                        <button type="submit" className={styles.saveBtn} disabled={savingMember || isDemo}>
                          {savingMember ? 'Adding...' : 'Add Member'}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className={members.length === 0 ? styles.emptyState : styles.addMemberRow}>
                      {members.length === 0 && maxMembers !== 0 && <p>No additional members added yet.</p>}
                      {(maxMembers === null || (maxMembers > 0 && members.length < maxMembers)) && (
                        <button className={styles.addBtn} onClick={() => setShowMemberForm(true)}>
                          Add Member
                        </button>
                      )}
                      {maxMembers !== null && maxMembers > 0 && members.length >= maxMembers && (
                        <p className={styles.memberLimitReached}>Member limit reached</p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {activeTab === 'privacy' && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2>Privacy Choices</h2>
                <p>Manage your privacy and notification preferences</p>
              </div>
              <div className={styles.privacyOptions}>
                <div className={styles.privacyOption}>
                  <div className={styles.privacyInfo}>
                    <h4>Email Notifications</h4>
                    <p>Receive updates about your orders and account</p>
                  </div>
                  <label className={styles.toggle}>
                    <input type="checkbox" defaultChecked />
                    <span className={styles.slider}></span>
                  </label>
                </div>
                <div className={styles.privacyOption}>
                  <div className={styles.privacyInfo}>
                    <h4>SMS Notifications</h4>
                    <p>Get text messages about shipping and delivery</p>
                  </div>
                  <label className={styles.toggle}>
                    <input type="checkbox" />
                    <span className={styles.slider}></span>
                  </label>
                </div>
                <div className={styles.privacyOption}>
                  <div className={styles.privacyInfo}>
                    <h4>Marketing Emails</h4>
                    <p>Receive promotional offers and health tips</p>
                  </div>
                  <label className={styles.toggle}>
                    <input type="checkbox" />
                    <span className={styles.slider}></span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
